import React from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
const Disclaimer: React.FC = () => {
  useDocumentTitle('Cognidaily | Disclaimer');
  const lastUpdated = 'January 2025';

  return (
    <main>
      <SEOHead
        title="Disclaimer"
        description="Important disclaimers regarding the use of Cognidaily's quiz platform, content accuracy, and user responsibilities."
        keywords="disclaimer, legal notice, content accuracy, quiz disclaimer, educational content"
        url="https://cognidaily.io/disclaimer"
      />

      <section className="legal-section">
        <div className="legal-container">
          <div className="legal-card">
            <h1 className="legal-main-title">Disclaimer</h1>
            <p className="legal-last-updated">Last Updated: {lastUpdated}</p>

            <div className="legal-intro">
              <p>
                Please read this Disclaimer carefully before using Cognidaily. By accessing or using our platform, you acknowledge that you have read, understood, and agree to be bound by the terms of this Disclaimer.
              </p>
            </div>

            <div className="legal-content">
              <section className="legal-content-section">
                <h2>1. Educational and Entertainment Purpose</h2>
                
                <p>
                  Cognidaily is designed for educational and entertainment purposes. The quizzes, questions, and content provided on our platform are intended to:
                </p>
                <ul>
                  <li>Stimulate intellectual curiosity and learning</li>
                  <li>Provide an engaging competitive experience</li>
                  <li>Offer mental exercise and cognitive challenges</li>
                  <li>Foster friendly competition among users</li>
                </ul>
                <p>
                  Our platform is not intended to serve as a substitute for formal education, professional training, or academic assessment.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>2. Content Accuracy and Reliability</h2>
                
                <h3>2.1 No Warranty of Accuracy</h3>
                <p>
                  While we strive to provide accurate and reliable quiz questions and content, we make no representations or warranties regarding:
                </p>
                <ul>
                  <li>The accuracy, completeness, or reliability of quiz questions</li>
                  <li>The correctness of answers or explanations</li>
                  <li>The currency of information presented</li>
                  <li>The suitability of content for any particular purpose</li>
                </ul>

                <h3>2.2 Content Sources</h3>
                <p>
                  Quiz questions and content may be derived from various sources, including publicly available information, educational materials, and general knowledge. Advanced technologies may be utilized in the curation and quality assurance of our question content. We do not guarantee that all information is current, accurate, or free from errors.
                </p>

                <h3>2.3 Subjectivity and Interpretation</h3>
                <p>
                  Some questions may involve matters of interpretation, opinion, or evolving knowledge. In such cases, we provide answers based on generally accepted information at the time of question creation, but these may not reflect all perspectives or the most recent developments.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>3. Quiz Results and Scores</h2>
                
                <h3>3.1 No Guarantee of Results</h3>
                <p>
                  Your quiz scores, rankings, and achievements on Cognidaily are based on your performance within our platform's system. These results:
                </p>
                <ul>
                  <li>Do not reflect your actual knowledge or intelligence</li>
                  <li>Are not intended for use in academic or professional contexts</li>
                  <li>May be influenced by factors such as timing, question selection, and technical issues</li>
                  <li>Should not be used as a measure of educational achievement</li>
                </ul>

                <h3>3.2 Scoring System</h3>
                <p>
                  Our scoring system is designed to create fair competition but may not perfectly reflect knowledge or ability. Scores are calculated based on accuracy and speed, and we reserve the right to adjust scores or rankings for technical reasons or to maintain platform integrity.
                </p>

                <h3>3.3 Leaderboard Rankings</h3>
                <p>
                  Leaderboard rankings are relative and based on performance within our platform. They do not represent objective measures of knowledge, intelligence, or educational achievement.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>4. No Professional Advice</h2>
                
                <p>
                  The content on Cognidaily does not constitute professional, legal, medical, financial, or any other form of professional advice. You should not rely on information from our platform for:
                </p>
                <ul>
                  <li>Making important decisions</li>
                  <li>Professional or academic purposes</li>
                  <li>Medical, legal, or financial matters</li>
                  <li>Any situation requiring professional expertise</li>
                </ul>
                <p>
                  Always consult qualified professionals for advice on matters requiring specialized knowledge or expertise.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>5. Platform Availability and Performance</h2>
                
                <h3>5.1 Service Interruptions</h3>
                <p>
                  We do not guarantee that Cognidaily will be available at all times or that it will be free from errors, interruptions, or technical issues. The platform may be unavailable due to:
                </p>
                <ul>
                  <li>Scheduled maintenance</li>
                  <li>Technical difficulties</li>
                  <li>Server outages</li>
                  <li>Force majeure events</li>
                </ul>

                <h3>5.2 Performance Issues</h3>
                <p>
                  We are not responsible for any delays, failures, or performance issues that may affect your ability to complete quizzes or access features. Such issues may impact your scores, rankings, or achievements, and we are not liable for any consequences arising from these issues.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>6. User Responsibility</h2>
                
                <h3>6.1 Independent Verification</h3>
                <p>
                  You are encouraged to independently verify any information you encounter on Cognidaily, especially if you intend to use it for important purposes. We are not responsible for any decisions or actions you take based on information from our platform.
                </p>

                <h3>6.2 Appropriate Use</h3>
                <p>
                  You are responsible for using Cognidaily in an appropriate manner and in accordance with our Terms of Service. We are not responsible for any consequences arising from misuse of the platform.
                </p>

                <h3>6.3 Technical Requirements</h3>
                <p>
                  You are responsible for ensuring that your device, browser, and internet connection meet the technical requirements necessary to use Cognidaily. We are not responsible for issues arising from incompatible hardware or software.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>7. Third-Party Content and Links</h2>
                
                <h3>7.1 External Links</h3>
                <p>
                  Cognidaily may contain links to third-party websites or resources. We are not responsible for:
                </p>
                <ul>
                  <li>The content, accuracy, or availability of third-party sites</li>
                  <li>The privacy practices of third-party sites</li>
                  <li>Any products, services, or information offered by third parties</li>
                  <li>Any damages or losses arising from your use of third-party sites</li>
                </ul>

                <h3>7.2 Third-Party Services</h3>
                <p>
                  We may use third-party services to operate and improve Cognidaily. We are not responsible for the performance, availability, or practices of these third-party services.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>8. Limitation of Liability</h2>
                
                <p>
                  To the maximum extent permitted by law, Cognidaily and its officers, directors, employees, and agents shall not be liable for:
                </p>
                <ul>
                  <li>Any errors or inaccuracies in quiz content</li>
                  <li>Any loss or damage resulting from your use of the platform</li>
                  <li>Any interruption or unavailability of the platform</li>
                  <li>Any decisions made based on information from the platform</li>
                  <li>Any indirect, incidental, special, or consequential damages</li>
                </ul>
                <p>
                  Your use of Cognidaily is at your own risk. The platform is provided "as is" and "as available" without warranties of any kind.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>9. Intellectual Property</h2>
                
                <p>
                  All content on Cognidaily, including quiz questions, answers, explanations, and other materials, is protected by intellectual property laws. While we strive to respect the intellectual property rights of others, we cannot guarantee that all content is free from potential claims. If you believe that any content on our platform infringes your rights, please contact us.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>10. Changes to Platform</h2>
                
                <p>
                  We reserve the right to modify, suspend, or discontinue any aspect of Cognidaily at any time, including:
                </p>
                <ul>
                  <li>Quiz questions and content</li>
                  <li>Scoring systems and algorithms</li>
                  <li>Features and functionality</li>
                  <li>Platform availability</li>
                </ul>
                <p>
                  We are not obligated to provide notice of such changes, and we are not liable for any impact these changes may have on your experience or results.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>11. Jurisdiction and Governing Law</h2>
                
                <p>
                  This Disclaimer shall be governed by and construed in accordance with the laws of Australia. Any disputes arising from or relating to this Disclaimer or your use of Cognidaily shall be subject to the exclusive jurisdiction of the courts of Australia.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>12. Updates to This Disclaimer</h2>
                
                <p>
                  We may update this Disclaimer from time to time to reflect changes in our practices, the platform, or for other operational, legal, or regulatory reasons. We will notify you of material changes by posting the new Disclaimer on this page and updating the "Last Updated" date. Your continued use of Cognidaily after such changes constitutes your acceptance of the updated Disclaimer.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>13. Contact Information</h2>
                
                <p>
                  If you have any questions about this Disclaimer, please contact us at:
                </p>
                <p>
                  <strong>Email:</strong> <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Disclaimer;

